// GeoLocator resolves where the job (or the workshop) is.
//
// Three inputs feed one answer, in order of precision:
//   1. a pin the user drops on the map          → exact coordinates
//   2. the browser's geolocation                → exact coordinates
//   3. Departamento + Distrito                  → the district's centroid
//
// The district is always filled in: dropping a pin snaps it to the nearest
// district, so the pin and the district can never disagree. That is why Distrito
// can be a required field without ever getting in the user's way.
//
// It deliberately never calls .focus(): the location section sits in the middle
// of a long form, and yanking the caret there on load fights the user who is
// still answering the first question.
//
// Island Architecture: a light-DOM web component, so its inputs still take part
// in the surrounding <form> submission.
import { LocationMap } from "./location-map";

type GeoStatusState = "loading" | "ok" | "error";

interface DistrictOption {
  ubigeo: string;
  name: string;
  province: string;
  department: string;
  label: string;
  lat: number;
  lng: number;
}

export class GeoLocator extends HTMLElement {
  private districts: DistrictOption[] = [];
  private map: LocationMap | null = null;
  /** True while Dirección holds a value we wrote, so the pin may replace it. */
  private addressIsOurs = true;
  private geocodeTimer = 0;
  private geocodeController: AbortController | null = null;

  private status!: HTMLElement;
  private lat!: HTMLInputElement;
  private lng!: HTMLInputElement;
  private ubigeo!: HTMLInputElement;
  private dept!: HTMLSelectElement;
  private district!: HTMLInputElement;
  private datalist!: HTMLDataListElement;
  private hint: HTMLElement | null = null;

  connectedCallback(): void {
    const status = this.querySelector<HTMLElement>("[data-geo-status]");
    const lat = this.querySelector<HTMLInputElement>("[data-geo-lat]");
    const lng = this.querySelector<HTMLInputElement>("[data-geo-lng]");
    const ubigeo = this.querySelector<HTMLInputElement>("[data-geo-ubigeo]");
    const dept = this.querySelector<HTMLSelectElement>("[data-geo-dept]");
    const district = this.querySelector<HTMLInputElement>("[data-geo-district]");
    const datalist = this.querySelector<HTMLDataListElement>("[data-geo-datalist]");
    if (!status || !lat || !lng || !ubigeo || !dept || !district || !datalist) return;

    this.status = status;
    this.lat = lat;
    this.lng = lng;
    this.ubigeo = ubigeo;
    this.dept = dept;
    this.district = district;
    this.datalist = datalist;
    this.hint = this.querySelector<HTMLElement>("[data-geo-district-hint]");

    this.initMap();
    this.wireDepartment();
    this.wireDistrict();
    this.wirePinButton();
    this.wireAddress();

    // Already located (returning from a validation error, or an existing
    // profile): trust what we have and do not re-probe the browser.
    if (this.lat.value && this.lng.value) {
      this.map?.setPin(Number(this.lat.value), Number(this.lng.value), 16);
      this.setStatus("Ubicación exacta guardada. Puedes moverla en el mapa.", "ok");
      this.loadDepartmentDistricts();
      return;
    }
    if (this.ubigeo.value) {
      this.setStatus("Ubicación guardada. Arrastra el pin para precisarla.", "ok");
      void this.loadDepartmentDistricts().then((): void => {
        // Centre on the saved district rather than leaving the pin on the default
        // view — otherwise "arrastra el pin" means dragging it across the country.
        const saved = this.districts.find((d) => d.ubigeo === this.ubigeo.value);
        if (saved) this.map?.setPin(saved.lat, saved.lng, 13);
      });
      return;
    }

    // A workshop is not wherever its owner happens to be sitting when they edit
    // their profile, so the manufacturer's map never auto-probes.
    if (this.hasAttribute("data-geo-manual-only")) {
      this.setStatus("Elige tu distrito y arrastra el pin hasta tu taller.", "loading");
      return;
    }
    this.detect();
  }

  // --- automatic detection --------------------------------------------------

  private detect(): void {
    if (!("geolocation" in navigator) || !window.isSecureContext) {
      this.setStatus("No podemos detectarte automáticamente. Elige tu distrito.", "error");
      return;
    }
    this.setStatus("Detectando tu ubicación…", "loading");
    navigator.geolocation.getCurrentPosition(
      (pos: GeolocationPosition): void => {
        // applyPin fills the address too, from wherever the pin lands.
        void this.applyPin(pos.coords.latitude, pos.coords.longitude, 16);
        this.setStatus("Ubicación detectada. Arrástrala en el mapa si no es exacta.", "ok");
      },
      (): void => {
        this.setStatus("No pudimos detectar tu ubicación. Elige tu distrito.", "error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  private wirePinButton(): void {
    const button = this.querySelector<HTMLButtonElement>("[data-geo-pin]");
    if (!button) return;
    button.addEventListener("click", (): void => this.detect());
  }

  // --- the map --------------------------------------------------------------

  private initMap(): void {
    const container = this.querySelector<HTMLElement>("[data-geo-map]");
    if (!container) return;
    this.map = new LocationMap(container, ({ lat, lng }): void => {
      // The pin is the most precise thing we have, so it wins — and the district
      // follows it rather than being left to contradict it.
      void this.applyPin(lat, lng, undefined, { fromUser: true });
    });
    // The map may start inside a hidden/collapsed box; Leaflet needs a nudge.
    requestAnimationFrame((): void => this.map?.refresh());
  }

  /** Writes the coordinates, moves the pin, and snaps the district to match. */
  private async applyPin(
    lat: number,
    lng: number,
    zoom?: number,
    opts: { fromUser?: boolean } = {},
  ): Promise<void> {
    this.lat.value = lat.toFixed(6);
    this.lng.value = lng.toFixed(6);
    if (!opts.fromUser) this.map?.setPin(lat, lng, zoom);

    // The pin knows where it is, so let it write the address too.
    void this.fillAddressFrom(lat, lng);

    const near = await this.nearestDistrict(lat, lng);
    if (!near) return;
    this.ubigeo.value = near.ubigeo;
    this.dept.value = near.department;
    await this.loadDepartmentDistricts();
    this.district.value = near.label;
    this.district.disabled = false;
    this.setHint(`Usaremos ${near.name} como referencia. El pin afina la distancia.`);
    if (opts.fromUser) {
      this.setStatus("Ubicación ajustada en el mapa.", "ok");
    }
  }

  private async nearestDistrict(lat: number, lng: number): Promise<DistrictOption | null> {
    try {
      const res = await fetch(`/api/distritos/cercano?lat=${lat}&lng=${lng}`);
      if (!res.ok) return null;
      return (await res.json()) as DistrictOption;
    } catch {
      return null;
    }
  }

  // --- the district picker --------------------------------------------------

  private wireDepartment(): void {
    this.dept.addEventListener("change", (): void => {
      this.district.value = "";
      this.ubigeo.value = "";
      // Drop the coordinates too. Switching department means the old pin is in
      // the wrong region entirely; keeping it would submit a position the user
      // can no longer see any relation to.
      this.lat.value = "";
      this.lng.value = "";
      void this.loadDepartmentDistricts();
    });
  }

  private async loadDepartmentDistricts(): Promise<void> {
    this.datalist.replaceChildren();
    if (!this.dept.value) {
      this.district.disabled = true;
      this.setHint("Elige primero el departamento.");
      return;
    }
    this.district.disabled = false;
    this.districts = await this.fetchDistricts(this.dept.value);
    for (const d of this.districts) {
      const opt = document.createElement("option");
      opt.value = d.label; // the datalist matches on what the user types
      this.datalist.append(opt);
    }
    if (!this.district.value) {
      this.setHint(`${this.districts.length} distritos · escribe para buscar.`);
    }
  }

  private wireDistrict(): void {
    this.district.addEventListener("input", (): void => {
      const typed = norm(this.district.value);
      const hit = this.districts.find((d) => norm(d.label) === typed)
        ?? this.districts.find((d) => norm(d.name) === typed);

      this.ubigeo.value = hit ? hit.ubigeo : "";
      if (!hit) {
        if (this.district.value.trim() !== "") this.setHint("Elige un distrito de la lista.");
        return;
      }

      // Choosing a district MOVES the pin to it — even if one was already placed.
      // Whatever the user touched last is what they mean: dropping a pin snaps the
      // district to match it, and picking a district moves the pin to match that.
      // Leaving a stale pin behind would quietly submit the wrong position.
      this.lat.value = hit.lat.toFixed(6);
      this.lng.value = hit.lng.toFixed(6);
      // Animated: two districts of the same city look almost the same from above, and
      // the pin never leaves the centre of the map. Without the flight, choosing a
      // district reads as "the map ignored me".
      this.map?.setPin(hit.lat, hit.lng, 13, { animate: true });
      // The pin moved, so the address follows it — otherwise Dirección would
      // still describe the district they just left.
      this.fillAddressFrom(hit.lat, hit.lng);
      this.setHint(`Usaremos ${hit.name} como referencia. Arrastra el pin para precisar.`);
      this.setStatus(`Ubicación en ${hit.name}. Arrastra el pin para precisarla.`, "ok");
    });
  }

  private async fetchDistricts(department: string): Promise<DistrictOption[]> {
    try {
      const res = await fetch(`/api/distritos?departamento=${encodeURIComponent(department)}`);
      if (!res.ok) return [];
      return (await res.json()) as DistrictOption[];
    } catch {
      return [];
    }
  }

  // --- helpers --------------------------------------------------------------

  /**
   * Fills Dirección from wherever the pin now stands (reverse geocoding).
   *
   * Only writes into a field that is empty or still holds a value *we* put there:
   * an address the customer typed themselves is theirs, and moving the pin must
   * not silently overwrite it. Typing in the field marks it as theirs for good.
   *
   * Debounced, and each call cancels the one before it: dragging the pin fires a
   * position per frame, and Nominatim's usage policy is one request per second.
   * The address is optional anyway — the district and the pin already locate the
   * job — so a failure here is silent by design.
   */
  private fillAddressFrom(lat: number, lng: number): void {
    const address = this.querySelector<HTMLInputElement>("[data-geo-address]");
    if (!address) return;
    if (address.value.trim() !== "" && !this.addressIsOurs) return;

    window.clearTimeout(this.geocodeTimer);
    this.geocodeController?.abort();

    this.geocodeTimer = window.setTimeout(async (): Promise<void> => {
      const controller = new AbortController();
      this.geocodeController = controller;
      const previous = address.placeholder;
      address.placeholder = "Buscando la dirección…";
      try {
        const url =
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=es`;
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = (await res.json()) as { display_name?: string };
        if (!data.display_name) return;
        // The user may have started typing while the lookup was in flight.
        if (address.value.trim() !== "" && !this.addressIsOurs) return;
        address.value = data.display_name;
        this.addressIsOurs = true;
      } catch {
        /* aborted, offline, or rate-limited: the address is optional */
      } finally {
        address.placeholder = previous;
      }
    }, 600);
  }

  /** Once the customer edits Dirección, it is theirs and the pin stops touching it. */
  private wireAddress(): void {
    const address = this.querySelector<HTMLInputElement>("[data-geo-address]");
    if (!address) return;
    // A value already on the field at load (returning from a validation error) is
    // the customer's, not ours.
    this.addressIsOurs = address.value.trim() === "";
    address.addEventListener("input", (): void => {
      this.addressIsOurs = false;
    });
  }

  private setStatus(text: string, state: GeoStatusState): void {
    this.status.textContent = text;
    this.status.dataset["state"] = state;
  }

  private setHint(text: string): void {
    if (this.hint) this.hint.textContent = text;
  }
}

// norm folds case and accents so "Ancón", "ancon" and "ANCON" all match.
function norm(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}
